import { useAppDispatch, useAppSelector } from '@store/hooks';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdEdit } from 'react-icons/md';
import { FaRegTrashCan } from 'react-icons/fa6';
import { UPDATE_HOSPITAL_USER_PATH, UPDATE_USER_PATH } from '@constants/routes';
import { adminDeleteUser, getUserName } from '@utils/utils';
import EditIconWhite from '@components/SvgComponents/EditIconWhite';
import { FiEdit } from 'react-icons/fi';
import { setDeviceDetail } from '@store/reducers/userSlice';
import ErrorMessage from '@components/Error/ErrorMessage';
import {
    addDeviceData,
    checkDeviceIdExists,
    checkHospitalDeviceIdExists,
    deviceDetailUpdate,
} from '@utils/deviceUtils';
import { scan } from '@utils/authUtils';
import {
    DEVICE_TABLE,
    HOSPITALS_TABLE,
    NURSE_DEVICE_TABLE,
    USER_INFO_TABLE,
} from '@constants/dynamoDBTableName';
import { IoMdCloseCircle } from 'react-icons/io';
import { APPROVED } from '@constants/constants';
import Swal from 'sweetalert2';
import Select, { SingleValue, MultiValue } from 'react-select';
import { hospitalUuid, nurseDeviceUpdate } from '@utils/hospitalUtils';
import {
    PutItemCommand,
    PutItemCommandInput,
    QueryCommand,
} from '@aws-sdk/client-dynamodb';
import { dbClient } from '@utils/awsClients';
import ModalPopUp from '@components/ModalPopUpComponent';
import {
    capitalizeFirstLetter,
    formatDateDMY,
    generateUniqueTimestamp,
    getDataUsingDeviceId,
    getDataUsingUserId,
    sendAssignNotification,
    updateTable,
} from '@utils/sharedUtils';
import AssignDeviceModal from '@components/AssignDeviceModal';
import { v4 as uuidv4 } from 'uuid';
import DataLoader from '@components/DataLoader';
import { useAuditLog } from '../../contexts/AuditLogContext';
import { sendPushNotification } from '@utils/notificationUtils';

const MySwal = Swal;

interface Nurse {
    tenant_id: string;
    name: string;
    uuid: string;
    user_id: string;
    nurse_id?: string;
}

interface Device {
    uuid: string;
    device_id: string;
    device_name?: string;
    hospital_id: string;
}

interface DeviceOption {
    value: string;
    label: string;
    hospitalId: string;
}

interface NurseOption {
    value: string;
    label: string;
    tenant_id: string;
    user_id: string;
}

interface DeviceTableProps {
    data: any[];
    header: any[];
    fetchAttributes: () => void; // Add this line
    totalData: any[];
    row: number;
}

interface NurseDevice {
    id: string;
    device_uuid: string;
    device_id: string;
    nurse_name: string;
    nurse_id: string;
    nurse_uuid: string;
    created_at: string;
    created_at_time: string;
    hospital_id: string;
    has_control?: boolean;
    controlling_nurse_name?: string;
}

interface GroupedDeviceData {
    device_uuid: string;
    device_id: string;
    controlling_nurse_name?: string;
    hospital_id: string;
    created_at_time: string;
    nurses: NurseDevice[];
}

const AssignedDeviceTable = ({
    data,
    header,
    fetchAttributes,
    totalData,
    row,
}: DeviceTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [updatedData, setData] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [tableId, setTableId] = useState('');
    const [allData, setAllData] = useState(totalData);
    const [rowPerPage, setRowPerPage] = useState(row);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nurseData, setNurseData] = useState<Nurse[]>([]);
    const [oldNurseData, setOldNurseData] = useState<Nurse[]>([]);
    const [deviceData, setDeviceData] = useState<Device[]>([]);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [oldSelectedDevice, setOldSelectedDevice] = useState<Device | null>(
        null
    );
    const [selectedNurses, setSelectedNurses] = useState<Nurse[]>([]);
    const [oldSelectedNurses, setOldSelectedNurses] = useState<Nurse[]>([]);
    const [selectedDeviceOption, setSelectedDeviceOption] =
        useState<SingleValue<DeviceOption>>(null);
    const [selectedNurseOptions, setSelectedNurseOptions] = useState<
        MultiValue<NurseOption>
    >([]);
    const loggedInUserInfoUuid = useAppSelector(
        (state) => state.login.loggedInUserInfoUuid
    );
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const role = useAppSelector((state) => state.login.loggedInUserRole);
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const { addLog } = useAuditLog();
    const loggedInUserEmail = useAppSelector(
        (state) => state.login.loggedInUserEmail
    );

    const fetchData = async () => {
        try {
            const data = await hospitalUuid(loggedInUserTenantID);
            if (data?.nurseList && data?.deviceList) {
                setNurseData(data?.nurseList);
                setDeviceData(data?.deviceList);
            }
            // setUserData(data);
        } catch (err: any) {
            console.log('err', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (data) {
            fetchData();
            setData(data);
        }
        if (totalData) setAllData(totalData);
        if (row) setRowPerPage(row);
    }, [data, totalData, row]);

    if (data.length === 0) {
        return (
            // <div className="card rounded-xl mt-5 p-4 mx-auto w-full">
            <div className="card rounded-xl mt-5 p-0 mx-auto w-full">
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="border-top">
                            <tr>
                                {header.map((device) => (
                                    <th
                                        className="font-semibold text-black text-base !normal-case"
                                        key={device.id}
                                    >
                                        {capitalizeFirstLetter(device.title)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="text-center">
                                <td colSpan={header.length + 1}>
                                    {t('user_management.user_table.no_data')}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Get the keys from the first object in the data array
    let keys = Object.keys(data[0]).filter(
        (key) =>
            key !== 'id' &&
            key !== 'hospital_id' &&
            key !== 'tenant_id' &&
            key !== 'user_id' &&
            key !== 'nurse_uuid' &&
            key !== 'device_uuid' &&
            key !== 'nurses'
    );

    const handleRowClick = async (device: any) => {
        //     console.log('device', device);
    };

    const getStatusClass = (status: string) => {
        if (!status) {
            return ''; // Return an empty string if status is undefined or null
        }
        // console.log('Status:', status);
        switch (status.toLowerCase().trim()) {
            case 'approved':
                return 'text-white !text-sm bg-success inline-block rounded-2xl px-3 py-1';
            case 'pending':
                return 'text-white !text-sm bg-warning inline-block rounded-2xl px-3 py-1';
            case 'blocked':
                return 'text-white !text-sm bg-secondary inline-block rounded-2xl px-3 py-1';
            default:
                return '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            if (!selectedDevice || selectedNurses.length === 0) {
                console.error('Please select both device and nurse');
                return;
            }
            setLoading(true);

            const checkDeviceParams = {
                TableName: 'nurse_device',
                IndexName: 'device_id-created_at-index',
                KeyConditionExpression: 'device_id = :device_id',
                ExpressionAttributeValues: {
                    ':device_id': { S: selectedDevice.uuid },
                },
            };
            const checkDeviceCommand = new QueryCommand(checkDeviceParams);
            const checkDeviceResponse = await dbClient.send(checkDeviceCommand);

            const newNurses = selectedNurses;
            const oldNurses = oldSelectedNurses;

            // const formattedDateTime = new Date().toISOString();
            const formattedDateTime = generateUniqueTimestamp();

            const nursesToUpdate: Nurse[] = [];
            const nursesToAdd = selectedNurses.filter((newNurse) => {
                // Check if the nurse is already assigned to the selected device
                const isAlreadyAssigned = checkDeviceResponse.Items?.some(
                    (item) =>
                        !item.deleted_at?.S &&
                        item.user_id.S === newNurse.user_id &&
                        item.device_id.S === selectedDevice.uuid
                );

                return !isAlreadyAssigned;
            });
            const nursesToRemove = oldNurses.filter(
                (oldNurse) =>
                    !newNurses.some(
                        (newNurse) => newNurse.uuid === oldNurse.uuid
                    )
            );

            const oldNurseData = oldNurses.filter((oldNurse) =>
                nursesToRemove.some(
                    (newNurse) => newNurse.uuid !== oldNurse.uuid
                )
            );

            const filteredData = updatedData.filter(
                (item) => item.device_uuid == tableId
            );

            for (const nurse of nursesToRemove) {
                // Perform deletion logic for old nurses not in the new list
                const updateExpression =
                    'SET #modified_by = :userId, #modified_at = :formattedDateTime, #deleted_by = :userId, #deleted_at = :formattedDateTime';
                const expressionAttributeNames = {
                    '#modified_by': 'modified_by',
                    '#modified_at': 'modified_at',
                    '#deleted_by': 'deleted_by',
                    '#deleted_at': 'deleted_at',
                };
                const expressionAttributeValues = {
                    ':userId': { S: loggedInUserTenantID },
                    ':formattedDateTime': { S: formattedDateTime },
                };

                if (filteredData.length > 0) {
                    const deviceData = filteredData[0]; // Assuming device_uuid is unique
                    const nurses = deviceData.nurses;

                    const nurseWithControl = nurses.find(
                        (item: any) =>
                            item.user_id == nurse.user_id &&
                            item.has_control == true
                    );

                    if (nurseWithControl && oldNurseData.length > 0) {
                        const detail = oldNurseData[0];
                        const tableId = detail.uuid;

                        const updateTableExpression =
                            'SET #modified_by = :userId, #modified_at = :formattedDateTime, #has_control = :has_control';
                        const expressionTableAttributeNames = {
                            '#modified_by': 'modified_by',
                            '#modified_at': 'modified_at',
                            '#has_control': 'has_control',
                        };
                        const expressionTableAttributeValues = {
                            ':userId': { S: loggedInUserTenantID },
                            ':formattedDateTime': { S: formattedDateTime },
                            ':has_control': { BOOL: true },
                        };

                        await updateTable(
                            NURSE_DEVICE_TABLE,
                            { uuid: { S: tableId } },
                            updateTableExpression,
                            expressionTableAttributeNames,
                            expressionTableAttributeValues
                        );

                        const logData = detail.user_id ? 
                            await getDataUsingUserId
                            (
                                USER_INFO_TABLE,
                                detail.user_id
                            ) : null;

                        await sendPushNotification({user_id: detail.user_id ? detail.user_id : '', type: 'assigned_device', device_id: selectedDevice?.device_id, reason: '', assigned_by: loggedInUserID });

                        addLog(
                            'User Device Assignment Updated',
                            tableId,
                            'Nurse Device',
                            loggedInUserID,
                            { 'Device': selectedDevice.device_id, 'User': logData?.Items?.[0]?.email?.S },
                            window.location.hostname
                        );
                    }
                }

                await updateTable(
                    NURSE_DEVICE_TABLE,
                    { uuid: { S: nurse.uuid } },
                    updateExpression,
                    expressionAttributeNames,
                    expressionAttributeValues
                );

                const logData = nurse.user_id ? 
                    await getDataUsingUserId
                    (
                        USER_INFO_TABLE,
                        nurse.user_id
                    ) : null;

                await sendPushNotification({user_id: nurse.user_id ? nurse.user_id : '', type: 'unlinked_device', device_id: selectedDevice?.device_id, reason: '', assigned_by: loggedInUserID });

                addLog(
                    'Nurse Unlinked from Device',
                    nurse.uuid,
                    'Nurse Device',
                    loggedInUserID,
                    { 'Device': selectedDevice.device_id, 'User': logData?.Items?.[0]?.email?.S },
                    window.location.hostname
                );
            }

            for (const nurse of nursesToAdd) {
                let hasControl = true;
                let isAlreadyAssigned = false;
                if (
                    checkDeviceResponse.Items &&
                    checkDeviceResponse.Items.length > 0
                ) {
                    for (const item of checkDeviceResponse.Items) {
                        if (
                            item.device_id.S === selectedDevice.uuid &&
                            !item.deleted_at?.S
                        ) {
                            if (item.has_control.BOOL) {
                                hasControl = false;
                            }
                        }
                    }
                }

                if (isAlreadyAssigned) {
                    setError(
                        'Device is already assigned to one of the selected nurses.'
                    );
                    return;
                }

                const id = uuidv4();

                const params: PutItemCommandInput = {
                    TableName: 'nurse_device',
                    Item: {
                        uuid: { S: id },
                        device_id: { S: selectedDevice.uuid },
                        hospital_id: { S: selectedDevice.hospital_id },
                        user_id: { S: nurse.user_id ? nurse.user_id : '' },
                        tenant_id: { S: loggedInUserTenantID },
                        has_control: { BOOL: hasControl },
                        created_at: { S: formattedDateTime },
                        created_by: { S: loggedInUserTenantID },
                        modified_at: { S: formattedDateTime },
                        modified_by: { S: loggedInUserTenantID },
                        deleted_at: { S: '' },
                        deleted_by: { S: '' },
                    },
                };

                const logData = nurse.user_id ? 
                    await getDataUsingUserId
                    (
                        USER_INFO_TABLE,
                        nurse.user_id
                    ) : null;

                await sendPushNotification({user_id: nurse.user_id ? nurse.user_id : '', type: 'assigned_device', device_id: selectedDevice?.device_id, reason: '', assigned_by: loggedInUserID });

                addLog(
                    'Device Assigned to User',
                    id,
                    'Nurse Device',
                    loggedInUserID,
                    { 'Device': selectedDevice.device_id, 'User': logData?.Items?.[0]?.email?.S },
                    window.location.hostname
                );

                const command = new PutItemCommand(params);
                await dbClient.send(command);
            }

            if (oldSelectedDevice) {
                for (const nurse of selectedNurses) {
                    let hasControl = true;
                    let isAlreadyAssigned = false;
                    if (
                        checkDeviceResponse.Items &&
                        checkDeviceResponse.Items.length > 0
                    ) {
                        for (const item of checkDeviceResponse.Items) {
                            if (
                                item.device_id.S === selectedDevice.uuid &&
                                !item.deleted_at?.S
                            ) {
                                if (item.user_id.S === nurse.user_id) {
                                    isAlreadyAssigned = true;
                                    break;
                                }
                                if (item.has_control.BOOL) {
                                    hasControl = false;
                                }
                            }
                        }
                    }

                    if (isAlreadyAssigned) {
                        setError(
                            'Device is already assigned to one of the selected nurses.'
                        );
                        return;
                    }

                    const res = await nurseDeviceUpdate(
                        NURSE_DEVICE_TABLE,
                        nurse.uuid,
                        loggedInUserTenantID,
                        selectedDevice.uuid,
                        nurse.user_id
                    );

                    const logData = nurse.user_id ? 
                        await getDataUsingUserId
                        (
                            USER_INFO_TABLE,
                            nurse.user_id
                        ) : null;

                    await sendPushNotification({user_id: nurse.user_id ? nurse.user_id : '', type: 'assigned_device', device_id: selectedDevice.device_id, reason: '', assigned_by: loggedInUserID });

                    addLog(
                        'User Device Assignment Updated',
                        nurse.uuid,
                        'Nurse Device',
                        loggedInUserID,
                        { 'Device': selectedDevice.device_id, 'User': logData?.Items?.[0]?.email?.S },
                        window.location.hostname
                    );

                    const email = logData?.Items?.[0]?.email?.S;
                    const toAddress = email ? [email] : [];

                    const notificationData = {
                        type: 'email',
                        users: nurse.user_id, // Sending recipient email/phone/deviceToken based on type
                        fromAddress: loggedInUserEmail,
                        toAddress: nurse.user_id,
                        schedule: null, // Include schedule if applicable
                    };
        
                    // Send the data to the sharedUtils.ts function
                    await sendAssignNotification(notificationData);
                }
            }

            await fetchAttributes();
            setShowModal(false);
            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'success',
                title: 'Assigned device updated successfully',
                showConfirmButton: false,
                timer: 1500,
                toast: true,
                customClass: {
                    popup: 'text-green bg-light_green', // Add custom class to the popup
                },
            });
        } catch (error: any) {
            console.error('Error checking device ID:', error);
            // throw new Error('Error checking device ID');
            let errorMessage;

            switch (error.name) {
                case 'ValidationException':
                    errorMessage =
                        'Validation error: Please check your request parameters.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Provisioned throughput exceeded: Please try again later.';
                    break;
                case 'ResourceNotFoundException':
                    errorMessage =
                        'Resource not found: Please check your table name.';
                    break;
                case 'InternalServerError':
                    errorMessage =
                        'Internal server error: Please try again later.';
                    break;
                case 'ServiceUnavailable':
                    errorMessage =
                        'Service unavailable: Please try again later.';
                    break;
                default:
                    errorMessage = `Unknown error: ${error.message || error}`;
            }
            setError(errorMessage);
            // return errorMessage;
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (nurseDeviceData: any) => {
        if (nurseDeviceData) {
            setSelectedDevice({
                uuid: nurseDeviceData.device_uuid,
                device_id: nurseDeviceData.device_id,
                hospital_id: nurseDeviceData.hospital_id, // Assuming hospital_id needs to be set separately
            });
            setSelectedDeviceOption({
                value: nurseDeviceData.device_uuid,
                label: `${nurseDeviceData.device_name} (${nurseDeviceData.device_id ? nurseDeviceData.device_id : nurseDeviceData.device_id})`,
                hospitalId: nurseDeviceData.hospital_id,
            });

            setOldNurseData(nurseData);

            const userIdsToExclude = nurseDeviceData.nurses.map(
                (nurse: any) => nurse.user_id
            );

            // Filter out nurses from nurseData whose user_id is in the userIdsToExclude array
            const updatedNurseData = nurseData.filter(
                (nurse: any) => !userIdsToExclude.includes(nurse.user_id)
            );

            setNurseData(updatedNurseData);

            setSelectedNurses(
                nurseDeviceData.nurses.map((option: any) => ({
                    uuid: option.id,
                    name: option.nurse_name,
                    tenant_id: option.tenant_id,
                    user_id: option.user_id,
                }))
            );

            setOldSelectedNurses(
                nurseDeviceData.nurses.map((option: any) => ({
                    uuid: option.id,
                    name: option.nurse_name,
                    tenant_id: option.tenant_id,
                    user_id: option.user_id,
                }))
            );

            setSelectedNurseOptions(
                nurseDeviceData.nurses.map((option: any) => ({
                    value: option.id,
                    label: `${option.nurse_name} (${option.nurse_id ? capitalizeFirstLetter(option.nurse_id) : option.nurse_id})`,
                    tenant_id: option.tenant_id,
                    user_id: option.user_id,
                }))
            );
            // setSelectedNurseOptions(
            //     nurseDeviceData.nurses.map((option: any) => ({
            //         value: option.id,
            //         label: `${option.nurse_name} (${option.nurse_id ? capitalizeFirstLetter(option.nurse_id) : option.nurse_id})`,
            //         tenant_id: option.tenantId,
            //         user_id: option.userId,
            //     }))
            // );

            setTableId(nurseDeviceData.device_uuid);

            // setError(null);
            setShowModal(true);
        }
    };

    const handleDeviceChange = async (selectedOption: any) => {
        setSelectedDevice({
            uuid: selectedOption.value,
            device_id: selectedOption.label,
            hospital_id: selectedOption.hospitalId, // Assuming hospital_id needs to be set separately
        });
        setOldSelectedDevice({
            uuid: selectedOption.value,
            device_id: selectedOption.label,
            hospital_id: selectedOption.hospitalId, // Assuming hospital_id needs to be set separately
        });
        setSelectedDeviceOption({
            value: selectedOption.value,
            label: `${selectedOption.device_name} (${selectedOption.device_id ? selectedOption.device_id : selectedOption.device_id})`,
            hospitalId: selectedOption.hospitalId, // Assuming hospital_id needs to be set separately
        });

        // Filter out nurses who are already assigned to the selected device
        try {
            setLoading(true);
            setError(null);
            const assignedNursesResponse = await getDataUsingDeviceId(
                NURSE_DEVICE_TABLE,
                selectedOption.value
            );

            // Get user_ids of nurses already assigned to this device
            const assignedUserIds = assignedNursesResponse.Items?.filter(
                (item) => !item.deleted_at?.S
            ).map((item) => item.user_id?.S) || [];

            // Use oldNurseData if available, otherwise use current nurseData as fallback
            const sourceNurseData = oldNurseData.length > 0 ? oldNurseData : nurseData;

            // Filter out nurses who are already assigned
            const filteredNurseData = sourceNurseData.filter(
                (nurse) => !assignedUserIds.includes(nurse.user_id)
            );

            setNurseData(filteredNurseData);
            
            // Check if any of the currently selected nurses are already assigned to this device
            if (selectedNurses.length > 0) {
                // Remove only the invalid nurses from selection
                const validNurses = selectedNurses.filter(
                    (nurse) => !assignedUserIds.includes(nurse.user_id)
                );
                setSelectedNurses(validNurses);

                // Update selected nurse options to match
                const validNurseOptions = selectedNurseOptions.filter(
                    (option) => !assignedUserIds.includes(option.user_id)
                );
                setSelectedNurseOptions(validNurseOptions);
            }
        } catch (error) {
            console.error('Error filtering nurses:', error);
            // On error, restore the original nurse data
            const sourceNurseData = oldNurseData.length > 0 ? oldNurseData : nurseData;
            setNurseData(sourceNurseData);
            setError('Error checking device assignments. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleNurseChange = (selectedOption: any) => {
        setSelectedNurses(
            selectedOption.map((option: any) => ({
                uuid: option.value,
                name: option.label,
                tenant_id: option.tenant_id,
                user_id: option.user_id,
            }))
        );

        setSelectedNurseOptions(
            selectedOption.map((option: any) => ({
                value: option.value,
                label: option.label,
                tenant_id: option.tenant_id,
                user_id: option.user_id,
            }))
        );
    };

    const close = () => {
        setShowModal(false);
        setNurseData(oldNurseData);
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            // const formattedDateTime = new Date().toISOString();
            const formattedDateTime = generateUniqueTimestamp();

            for (const nurse of selectedNurses) {
                // Perform deletion logic for old nurses not in the new list
                const updateExpression =
                    'SET #modified_by = :userId, #modified_at = :formattedDateTime, #deleted_by = :userId, #deleted_at = :formattedDateTime';
                const expressionAttributeNames = {
                    '#modified_by': 'modified_by',
                    '#modified_at': 'modified_at',
                    '#deleted_by': 'deleted_by',
                    '#deleted_at': 'deleted_at',
                };
                const expressionAttributeValues = {
                    ':userId': { S: loggedInUserTenantID },
                    ':formattedDateTime': { S: formattedDateTime },
                };

                await updateTable(
                    NURSE_DEVICE_TABLE,
                    { uuid: { S: nurse.uuid } },
                    updateExpression,
                    expressionAttributeNames,
                    expressionAttributeValues
                );

                const logData = nurse.user_id ? 
                    await getDataUsingUserId
                    (
                        USER_INFO_TABLE,
                        nurse.user_id
                    ) : null;

                await sendPushNotification({user_id: nurse.user_id ? nurse.user_id : '', type: 'unlinked_device', device_id: selectedDevice?.device_id, reason: '', assigned_by: loggedInUserID });

                addLog(
                    'Nurse Unlinked from Device',
                    nurse.uuid,
                    'Nurse Device',
                    loggedInUserID,
                    { 'Device': selectedDevice?.device_id, 'User': logData?.Items?.[0]?.email?.S },
                    window.location.hostname
                );
            }

            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'success',
                title: 'Assigned device deleted successfully',
                showConfirmButton: false,
                timer: 1500,
                toast: true,
                customClass: {
                    popup: 'text-green bg-light_green', // Add custom class to the popup
                },
            });
            await fetchAttributes();
            setShowModal(false);
            setShowDeleteModal(false);
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteUser = (data: any) => {
        setSelectedNurses(
            data.nurses.map((option: any) => ({
                uuid: option.id,
                name: option.nurse_name,
                tenant_id: option.tenant_id,
                user_id: option.user_id,
            }))
        );

        setSelectedDevice({
            uuid: data.value,
            device_id: data.label,
            hospital_id: data.hospitalId, // Assuming hospital_id needs to be set separately
        });
        // setTableId(data.id);
        setShowDeleteModal(true);
    };
    const tableHeight =
        allData.length >= 10 && rowPerPage === 10
            ? '36rem'
            : allData.length >= 5 && rowPerPage === 5
              ? '20rem'
              : 'auto';
    return (
        <>
            <div
                className="card rounded-xl mt-5 p-0 mx-auto w-full"
                style={{ height: tableHeight }}
            >
                <div className="card-datatable table-responsive">
                    <table className="datatables-users table">
                        <thead className="m-3">
                            <tr>
                                {header.map((device) => (
                                    <th
                                        className="text-black text-base font-semibold py-3 !normal-case"
                                        key={device.id}
                                    >
                                        {capitalizeFirstLetter(device.title)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {updatedData.map((device) => (
                                <tr key={device.device_uuid}>
                                    {keys.map((key) => {
                                        const value = device[key];
                                        return (
                                            <td
                                                key={key}
                                                onClick={() =>
                                                    handleRowClick(device)
                                                }
                                                // className="cursor-pointer"
                                            >
                                                <div
                                                    className={`text-base font-normal text-table_header
                                                    ${getStatusClass(value)}`}
                                                    dangerouslySetInnerHTML={{
                                                        __html: value || '--',
                                                    }}
                                                >
                                                    {/* {value ?? 'N/A'} */}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div className="flex space-x-5 items-center">
                                            <div
                                                className="cursor-pointer"
                                                onClick={() => {
                                                    handleEdit(device);
                                                }}
                                            >
                                                <MdEdit className="text-black" />
                                            </div>

                                            <div
                                                className="cursor-pointer"
                                                onClick={() =>
                                                    deleteUser(device)
                                                }
                                            >
                                                <FaRegTrashCan className="text-danger" />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            <tr></tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <AssignDeviceModal
                title="Update Assigned Device"
                btn_title="Update"
                showModal={showModal}
                onClose={close}
                error={error}
                handleSubmit={handleSubmit}
                handleDeviceChange={handleDeviceChange}
                handleNurseChange={handleNurseChange}
                deviceData={deviceData}
                nurseData={nurseData}
                selectedDeviceOption={selectedDeviceOption}
                selectedNurseOptions={selectedNurseOptions}
            />
            <ModalPopUp
                show={showDeleteModal}
                message={t('pop_up.nurse_device_delete_msg')}
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteModal(false)}
            />
            {loading && <DataLoader />}
        </>
    );
};

export default AssignedDeviceTable;
